import { z } from 'zod'
import { oc } from '@orpc/contract'
import { artifactsSchema } from '../../model/artifacts/artifacts.schema'
import { detailsMetaSchema } from './patient-clinical-summary-details.contract'

/**
 * Wire schema of the stored Risks & Gaps guidelines provenance, mirroring
 * `model/risks-gaps/types.ts` field-for-field. Deliberately DUPLICATED (like
 * detailsMetaSchema): the model type's import chain reaches middleware-internal `@/`
 * aliases that the web (which bundles *.contract.ts across the package boundary) cannot
 * resolve. The drift guard lives in the handler file.
 */
export const risksGapsGuidelinesMetaSchema = z.object({
  registryVersion: z.number(),
  matchedFamilies: z.array(z.object({ slug: z.string(), label: z.string() })),
  selectedDocuments: z.array(
    z.object({
      id: z.number(),
      source: z.string(),
      title: z.string(),
      url: z.string(),
      version: z.string(),
    }),
  ),
  searchedDocuments: z.array(z.object({ id: z.number(), contentHash: z.string() })),
  omittedClinicalUnits: z.number(),
  measures: z.array(z.object({ id: z.number(), contentHash: z.string() })).optional(),
})

/**
 * Zod schema for the stored RisksGapsReport, mirroring `model/risks-gaps/types.ts`
 * field-for-field (same duplication rationale as above; drift guard in the handler).
 */
export const risksGapsReportSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  artifacts: artifactsSchema,
  generatedAt: z.string(),
  clinicalSummaryDetailsHash: z.string(),
  guidelinesHash: z.string(),
  // Transparency blocks — present on reports written with them, absent on placeholders.
  detailsMeta: detailsMetaSchema.optional(),
  guidelinesMeta: risksGapsGuidelinesMetaSchema.optional(),
  beingSuperseded: z.boolean(),
  beingSupersededSince: z.string().optional(),
})

// Staleness/regeneration state shared by both endpoints — the exact overview state
// shape (see patient-clinical-summary-overview.contract.ts for field semantics), with
// `upToDate` covering BOTH staleness inputs: the clinical-details hash AND the
// guidelines side (selection hash + search-cited documents still active/unchanged).
const reportStateShape = {
  exists: z.boolean(),
  upToDate: z.boolean(),
  beingSuperseded: z.boolean(),
  beingSupersededSince: z.string().optional(),
  beingSupersededThinking: z.string().optional(),
  // The last rebuild FAILED (and no newer build has started): `at` + a short reason
  // ('Timed out', 'The response was too long to complete.', …). While present the
  // server declines non-forced refreshes — only the user's explicit Refresh retries —
  // and the UI shows "Failed to refresh" in place of the staleness badge.
  lastBuildFailed: z.object({ at: z.string(), reason: z.string() }).optional(),
}

// GET query params arrive as strings via OpenAPILink and no global coercion plugin is
// configured, so accept both wire forms and normalize to boolean.
const checkUpToDateParam = z
  .union([z.boolean(), z.enum(['true', 'false']).transform((v) => v === 'true')])
  .optional()

// Current Risks & Gaps report for a patient plus its staleness state. `risksGapsReport`
// is present iff `exists`. Computing `upToDate` means assembling the full details
// dataset AND re-running the guideline selection to hash them — slow — so it's opt-in
// via `checkUpToDate` (default false), letting the UI paint the stored report fast and
// verify staleness in a follow-up call.
export const patientRisksGaps = oc
  .route({ method: 'GET', path: '/v2/patient/{id}/risks-gaps' })
  .input(z.object({ id: z.string(), checkUpToDate: checkUpToDateParam }))
  .output(
    z.object({
      risksGapsReport: risksGapsReportSchema.optional(),
      ...reportStateShape,
      upToDate: z.boolean().optional(),
    }),
  )

// Kick off an asynchronous report rebuild unless the stored one is already up to date
// (`force` rebuilds regardless) or a build is already in flight. Admin route: the build
// runs under the Medplum ClientApplication identity.
export const patientRisksGapsRefresh = oc
  .route({ method: 'POST', path: '/v2/patient/{id}/risks-gaps/refresh' })
  .input(z.object({ id: z.string(), force: z.boolean().optional() }))
  .output(z.object(reportStateShape))
